/**
 * VisitorDrawer — Vaul-based bottom drawer for visitor panels.
 * Provides swipe-to-dismiss on mobile, drag handle, and consistent styling.
 * Drop-in replacement for Sheet in visitor components.
 */
import { useSyncExternalStore } from "react";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
} from "@wondermaps/ui";

/** Read --drawer-handle-color CSS variable from :root (set by VisitorMapBySlug) */
function getDrawerHandleColor() {
  return getComputedStyle(document.documentElement).getPropertyValue("--drawer-handle-color").trim() || undefined;
}
const subscribe = (cb: () => void) => {
  const obs = new MutationObserver(cb);
  obs.observe(document.documentElement, { attributes: true, attributeFilter: ["style"] });
  return () => obs.disconnect();
};
function useDrawerHandleColor() {
  return useSyncExternalStore(subscribe, getDrawerHandleColor, () => undefined);
}

export interface VisitorDrawerProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Disable backdrop interaction (e.g. DirectionsPanel) */
  modal?: boolean;
  /** Max height CSS class, default "max-h-[70vh]". Ignored when snapPoints provided. */
  maxHeightClass?: string;
  /** Full height mode (e.g. VisitorListView). Ignored when snapPoints provided. */
  fullHeight?: boolean;
  /** Additional className for DrawerContent */
  className?: string;
  children: React.ReactNode;
  /** Vaul snap points — pixel strings for fixed peeks, fractions for proportional. */
  snapPoints?: (number | string)[];
  /** Currently active snap point (controlled). */
  activeSnapPoint?: number | string | null;
  /** Called when snap point changes via gesture or programmatic set. */
  onSnapPointChange?: (point: number | string | null) => void;
  /** Index in snapPoints from which overlay fades in. Defaults to 1. */
  fadeFromIndex?: number;
  /** Accessible title for the drawer (replaces sr-only "Panel"). */
  title?: string;
  /** When false, prevents swipe-to-dismiss and outside-click close. */
  dismissible?: boolean;
  /** Accent color for the drag handle. */
  handleColor?: string;
  /** Inline style applied to DrawerContent root (e.g. brand bg color). */
  contentStyle?: React.CSSProperties;
}

export function VisitorDrawer({
  open,
  onOpenChange,
  modal = true,
  maxHeightClass = "max-h-[70vh]",
  fullHeight = false,
  className = "",
  children,
  snapPoints,
  activeSnapPoint,
  onSnapPointChange,
  fadeFromIndex = 1,
  title = "Panel",
  dismissible,
  handleColor,
  contentStyle,
}: VisitorDrawerProps) {
  const hasSnap = snapPoints && snapPoints.length > 0;

  const cssHandleColor = useDrawerHandleColor();
  const resolvedHandleColor = handleColor || cssHandleColor;

  const contentClassName = hasSnap
    ? `h-full flex flex-col p-0 border-0 ${className}`
    : fullHeight
      ? `h-[100dvh] flex flex-col p-0 border-0 gap-0 ${className}`
      : `h-auto ${maxHeightClass} flex flex-col p-0 border-0 ${className}`;

  if (hasSnap) {
    return (
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        modal={modal}
        shouldScaleBackground={false}
        snapPoints={snapPoints}
        activeSnapPoint={activeSnapPoint ?? snapPoints[0]}
        setActiveSnapPoint={onSnapPointChange ?? undefined}
        fadeFromIndex={fadeFromIndex}
        dismissible={dismissible}
      >
        <DrawerContent className={contentClassName} handleColor={resolvedHandleColor} style={contentStyle}>
          <DrawerTitle className="sr-only">{title}</DrawerTitle>
          {children}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      modal={modal}
      shouldScaleBackground={false}
      dismissible={dismissible}
    >
      <DrawerContent className={contentClassName} handleColor={resolvedHandleColor} style={contentStyle}>
        <DrawerTitle className="sr-only">{title}</DrawerTitle>
        {children}
      </DrawerContent>
    </Drawer>
  );
}
